//src/components/chat/sections/DailyOutfit.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { renderMessages } from '../utils/messageHelpers';
import RegisterSteps from './dailyOutfit/RegisterSteps';
import WeeklyOutfitGenerator from './dailyOutfit/WeeklyOutfitGenerator';
import { getProductImageUrl } from '../utils/productUtils';
import {
    FacebookShareButton,
    TwitterShareButton,
    WhatsappShareButton,
    PinterestShareButton,
    TelegramShareButton,
    FacebookIcon,
    TwitterIcon,
    WhatsappIcon,
    PinterestIcon,
    TelegramIcon
} from 'react-share';

// Компонент для отображения изображения товара с поддержкой запасных вариантов
const ProductImage = ({ 
  product, 
  alt, 
  className, 
  fallbackSrc = '/products/placeholder.png', 
  ...props 
}) => {
  const [imgSrc, setImgSrc] = useState(() => getProductImageUrl(product));
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Обновление источника при изменении товара
    if (product && !hasError) {
      setImgSrc(getProductImageUrl(product));
    }
  }, [product, hasError]);

  // Обработчик ошибки загрузки изображения
  const handleError = () => {
    if (!hasError) {
      console.warn(`Failed to load image for ${product?.name || 'unknown product'}, using fallback`);
      setHasError(true);
      setImgSrc(fallbackSrc);
    }
  };

  return (
    <img 
      src={imgSrc} 
      alt={alt || product?.name || 'Product image'} 
      className={className}
      onError={handleError}
      {...props}
    />
  );
};

// Вспомогательный компонент для отображения карточки образа
const OutfitCard = ({ 
  outfit, 
  index, 
  formatDate, 
  isDaily = false 
}) => {
  // Получаем базовый URL сайта для полных ссылок
  const baseUrl = window.location.origin;
  
  // Получаем полный URL изображения
  const getFullImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return path.startsWith('/') ? `${baseUrl}${path}` : `${baseUrl}/${path}`;
  };
  
  // Обработчик ошибки загрузки изображения
  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = '/products/placeholder.png';
  };
  
  // Путь к изображению образа
  const collagePath = outfit.collagePath || outfit.look_img || '';
  const imageUrl = getFullImageUrl(collagePath);
  
  // Описание образа
  const description = outfit.summary || outfit.description || outfit.look_description || "No description available";
  
  // Форматирование даты (только для ежедневных образов)
  const dateString = isDaily && outfit.date ? formatDate(outfit.date) : '';
  
  // Рендеринг ссылок на продукты
  const renderProductLinks = () => {
    try {
      // Определяем источник ссылок на продукты (разные поля в разных версиях API)
      let productLinks = null;
      
      if (outfit.productLinks && typeof outfit.productLinks === 'object') {
        productLinks = outfit.productLinks;
      } else if (outfit.products_links && typeof outfit.products_links === 'object') {
        productLinks = outfit.products_links;
      } else if (typeof outfit.productLinks === 'string') {
        try {
          productLinks = JSON.parse(outfit.productLinks);
        } catch (e) {
          console.error(`Error parsing productLinks string:`, e);
        }
      } else if (typeof outfit.products_links === 'string') {
        try {
          productLinks = JSON.parse(outfit.products_links);
        } catch (e) {
          console.error(`Error parsing products_links string:`, e);
        }
      }
      
      // Если нет ссылок, показываем сообщение
      if (!productLinks || typeof productLinks !== 'object' || Object.keys(productLinks).length === 0) {
        return <span className="no-links">No product links available</span>;
      }
      
      // Конвертируем в массив пар [имя, ссылка]
      const links = Object.entries(productLinks);
      
      // Рендерим ссылки
      return links.map(([name, link], i) => (
        <a 
          key={`product-${index}-${i}`} 
          href={typeof link === 'string' ? link : '#'} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="product-link"
        >
          {name && name.length > 20 ? name.substring(0, 20) + '...' : name || "Product"}
        </a>
      ));
    } catch (error) {
      console.error("Error rendering product links:", error);
      return <span className="error">Error displaying product links</span>;
    }
  };
  
  return (
    <div className={isDaily ? "daily-outfit-card" : "weekly-outfit-card"}>
      {isDaily && dateString && (
        <div className="outfit-date">{dateString}</div>
      )}
      {!isDaily && outfit.title && (
        <div className="outfit-title">{outfit.title}</div>
      )}
      
      <img 
        src={imageUrl} 
        alt={`${isDaily ? 'Daily' : 'Weekly'} outfit`} 
        className="outfit-image" 
        onError={handleImageError}
      />
      
      <div className="outfit-description">
        {description}
      </div>
      
      <div className="outfit-links">
        {renderProductLinks()}
      </div>
      
      <div className="share-buttons">
        <FacebookShareButton url={imageUrl} quote={description}>
          <FacebookIcon size={24} round />
        </FacebookShareButton>
        <TwitterShareButton url={imageUrl} title={description}>
          <TwitterIcon size={24} round />
        </TwitterShareButton>
        <WhatsappShareButton url={imageUrl} title={description}>
          <WhatsappIcon size={24} round />
        </WhatsappShareButton>
        <PinterestShareButton url={imageUrl} media={imageUrl} description={description}>
          <PinterestIcon size={24} round />
        </PinterestShareButton>
        <TelegramShareButton url={imageUrl} title={description}>
          <TelegramIcon size={24} round />
        </TelegramShareButton>
      </div>
    </div>
  );
};

const DailyOutfit = ({ 
    profileData, 
    setProfileData,
    messages, 
    userLooks, 
    isButtonVisible,
    setIsButtonVisible, 
    email,
    setEmail,
    stage,
    setStage,
    addMessage,
    setMessages
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [betaError, setBetaError] = useState(false);
    const [incompleteFields, setIncompleteFields] = useState([]);
    const [currentField, setCurrentField] = useState(null);
    const [weeklyOutfits, setWeeklyOutfits] = useState([]);
    const [dailyOutfits, setDailyOutfits] = useState([]);
    const [hasWeeklyOutfits, setHasWeeklyOutfits] = useState(false);
    const [weeklyOutfitsExpiryDate, setWeeklyOutfitsExpiryDate] = useState(null);

    // Check if profile is complete when profileData changes
    useEffect(() => {
        if (profileData && stage === 5) {
            checkProfileCompleteness();
        }
    }, [profileData, stage]);

    // Fetch weekly and daily outfits when user is authenticated
    useEffect(() => {
        if (email && stage === 5 && incompleteFields.length === 0 && !currentField) {
            fetchWeeklyOutfits();
            fetchDailyOutfits();
        }
    }, [email, stage, incompleteFields, currentField]);

    // Function to fetch weekly outfits
    const fetchWeeklyOutfits = async () => {
        try {
            const response = await axios.get('/getWeeklyOutfits', {
                params: { email }
            });
            
            if (response.data && response.data.outfits) {
                // Обрабатываем случай, когда outfits - это строка
                let outfitsData = response.data.outfits;
                
                // Если outfits пришел как строка, парсим его
                if (typeof outfitsData === 'string') {
                    try {
                        outfitsData = JSON.parse(outfitsData);
                    } catch (e) {
                        console.error('Error parsing outfits data:', e);
                        outfitsData = [];
                    }
                }
                
                // Если это пустой массив, устанавливаем hasWeeklyOutfits в false
                if (!Array.isArray(outfitsData) || outfitsData.length === 0) {
                    setWeeklyOutfits([]);
                    setHasWeeklyOutfits(false);
                    return;
                }
                
                // Ensure each outfit has the required properties
                const sanitizedOutfits = outfitsData.map(outfit => ({
                    ...outfit,
                    description: outfit.summary || '',
                    collagePath: outfit.collagePath || '',
                    productLinks: outfit.productLinks && typeof outfit.productLinks === 'object' 
                        ? outfit.productLinks 
                        : {}
                }));
                
                setWeeklyOutfits(sanitizedOutfits);
                setHasWeeklyOutfits(sanitizedOutfits.length > 0);
                
                if (response.data.expiryDate) {
                    setWeeklyOutfitsExpiryDate(new Date(response.data.expiryDate));
                }
            } else {
                setWeeklyOutfits([]);
                setHasWeeklyOutfits(false);
            }
        } catch (error) {
            console.error('Error fetching weekly outfits:', error);
            setWeeklyOutfits([]);
            setHasWeeklyOutfits(false);
        }
    };

    // Функция для получения ежедневных образов
    const fetchDailyOutfits = async () => {
        try {
            console.log('Fetching daily outfits for email:', email);
            
            // Try the API route first
            try {
                const response = await axios.get('/api/getDailyOutfits', {
                    params: { email }
                });
                
                console.log('Daily outfits response:', response.data);
                
                // Check if the response is valid JSON array
                if (Array.isArray(response.data)) {
                    processAndSetOutfits(response.data);
                    return;
                }
            } catch (apiError) {
                console.error('Error with /api/getDailyOutfits route:', apiError);
            }
            
            // Fallback to the old route
            try {
                const fallbackResponse = await axios.get('/getDailyOutfits', {
                    params: { email }
                });
                
                console.log('Fallback daily outfits response:', fallbackResponse.data);
                
                // Check if the response is valid JSON array
                if (Array.isArray(fallbackResponse.data)) {
                    processAndSetOutfits(fallbackResponse.data);
                    return;
                }
            } catch (fallbackError) {
                console.error('Error with fallback /getDailyOutfits route:', fallbackError);
            }
            
            // If we get here, both routes failed
            console.error('Failed to fetch daily outfits from both routes');
            setDailyOutfits([]);
        } catch (error) {
            console.error('Error in fetchDailyOutfits:', error);
            setDailyOutfits([]);
        }
    };
    
    const processAndSetOutfits = (outfitsData) => {
        const sanitizedOutfits = outfitsData.map(outfit => {
            console.log('Processing daily outfit:', outfit);
            
            // Handle productLinks - ensure it's an object
            let productLinks = {};
            
            // First try to parse from string if needed
            if (typeof outfit.productLinks === 'string') {
                try {
                    productLinks = JSON.parse(outfit.productLinks);
                    console.log('Successfully parsed productLinks from string');
                } catch (e) {
                    console.error('Error parsing productLinks string:', e);
                }
            } 
            // Then try as an object
            else if (outfit.productLinks && typeof outfit.productLinks === 'object') {
                productLinks = outfit.productLinks;
                console.log('Using productLinks as object directly');
            }
            // Try alternative field names
            else if (typeof outfit.products_links === 'string') {
                try {
                    productLinks = JSON.parse(outfit.products_links);
                    console.log('Successfully parsed products_links from string');
                } catch (e) {
                    console.error('Error parsing products_links string:', e);
                }
            } 
            else if (outfit.products_links && typeof outfit.products_links === 'object') {
                productLinks = outfit.products_links;
                console.log('Using products_links as object directly');
            }
            
            console.log('Final productLinks:', productLinks);
            
            return {
                id: outfit.id || 0,
                collagePath: outfit.collagePath || outfit.look_img || '',
                description: outfit.description || outfit.look_description || outfit.summary || '',
                summary: outfit.description || outfit.look_description || outfit.summary || '',
                date: outfit.date || outfit.look_date || new Date().toISOString(),
                productLinks,
                title: outfit.title || `Daily Outfit (${new Date(outfit.date || outfit.look_date || new Date()).toLocaleDateString()})`
            };
        });
        
        console.log('Final sanitized daily outfits:', sanitizedOutfits);
        setDailyOutfits(sanitizedOutfits);
    };

    // Function to check if all required profile fields are completed
    const checkProfileCompleteness = () => {
        const requiredFields = ['gender', 'style', 'city', 'colors'];
        const missing = [];

        requiredFields.forEach(field => {
            if (!profileData[field]) {
                missing.push(field);
            }
        });

        setIncompleteFields(missing);

        if (missing.length > 0) {
            // Start the completion process for the first missing field
            promptForMissingField(missing[0]);
        }
    };

    // Function to prompt user for a specific missing field
    const promptForMissingField = (field) => {
        setCurrentField(field);
        
        let message = '';
        switch (field) {
            case 'gender':
                message = "I noticed we don't have your gender preference saved. This helps us create better outfit suggestions. What's your gender?";
                setStage(1);
                break;
            case 'style':
                message = "Let's update your style preference. What style do you prefer?";
                setStage(2);
                break;
            case 'city':
                message = "I need your city to provide accurate weather-based outfit suggestions. Which city are you in?";
                setStage(3);
                break;
            case 'colors':
                message = "One last thing - let's pick your favorite color palette for outfit suggestions.";
                setStage(4);
                break;
            default:
                break;
        }
        
        if (message) {
            addMessage(message, 'bot');
        }
    };

    // Обработчик отправки email
    const handleEmailSubmit = async () => {
        setIsLoading(true);
        setBetaError(false);
        
        try {
            addMessage(`My email is ${email}`, 'user');
            
            // Проверяем, существует ли пользователь с таким email
            const checkResponse = await axios.get('/checkEmailExists', { 
                params: { email } 
            });
            
            const emailExists = checkResponse.data?.exists;
            
            if (!emailExists) {
                // Пользователь не найден - показываем сообщение о закрытой бете
                setBetaError(true);
                addMessage("🔒 We're currently running a closed beta test with our first focus group. You can sign up for our second group which will start very soon!", 'bot');
                return;
            }
            
            // Получаем данные пользователя
            const profileResponse = await axios.get('/api/profile', { 
                params: { email } 
            });
            
            if (profileResponse.data) {
                // Пользователь существует и у него есть профиль
                setProfileData(profileResponse.data);
                addMessage("🎉 Welcome back! Ready to plan your wardrobe for the week?", 'bot');
                
                // Переходим на этап проверки полноты профиля
                setStage(5);
            } else {
                // У пользователя нет профиля, начинаем регистрацию
                addMessage("Let's get to know you better! First, what's your gender?", 'bot');
                setStage(1);
            }
        } catch (error) {
            console.error('Error during email submission:', error);
            addMessage("Sorry, there was a problem connecting to our service. Please try again.", 'bot');
        } finally {
            setIsLoading(false);
        }
    };

    // Обработчик выбора пола
    const handleGenderClick = async (gender) => {
        setProfileData(prev => ({ ...prev || {}, gender, email }));
        addMessage(`My gender is ${gender}`, 'user');
        
        // If updating an existing profile
        if (currentField === 'gender') {
            try {
                await updateProfileField('gender', gender);
                proceedToNextIncompleteField('gender');
            } catch (error) {
                console.error('Error updating gender:', error);
                addMessage("Sorry, there was a problem updating your profile. Please try again.", 'bot');
            }
        } else {
            // Normal registration flow
            addMessage("Awesome! Let's talk about your style.", 'bot');
            setStage(2);
        }
    };

    // Обработчик выбора стиля
    const handleStyleClick = async (style) => {
        setProfileData(prev => ({ ...prev, style }));
        addMessage(`I really enjoy ${style}`, 'user');
        
        // If updating an existing profile
        if (currentField === 'style') {
            try {
                await updateProfileField('style', style);
                proceedToNextIncompleteField('style');
            } catch (error) {
                console.error('Error updating style:', error);
                addMessage("Sorry, there was a problem updating your profile. Please try again.", 'bot');
            }
        } else {
            // Normal registration flow
            addMessage("Great choice! Now, which city are you in? I need it for the weather forecast.", 'bot');
            setStage(3);
        }
    };

    // Обработчик выбора города
    const handleCityClick = async (city) => {
        setProfileData(prev => ({ ...prev, city }));
        addMessage(`I live in ${city}`, 'user');
        
        // If updating an existing profile
        if (currentField === 'city') {
            try {
                await updateProfileField('city', city);
                proceedToNextIncompleteField('city');
            } catch (error) {
                console.error('Error updating city:', error);
                addMessage("Sorry, there was a problem updating your profile. Please try again.", 'bot');
            }
        } else {
            // Normal registration flow
            addMessage("Perfect! Let's pick your favorite color palette.", 'bot');
            setStage(4);
        }
    };

    // Обработчик выбора цветов
    const handleColorClick = async (colors) => {
        const updatedProfileData = { ...profileData, colors, email };
        setProfileData(updatedProfileData);
        addMessage(`Here are my favorite colors: ${colors}`, 'user');
        
        // If updating an existing profile
        if (currentField === 'colors') {
            try {
                await updateProfileField('colors', colors);
                proceedToNextIncompleteField('colors');
            } catch (error) {
                console.error('Error updating colors:', error);
                addMessage("Sorry, there was a problem updating your profile. Please try again.", 'bot');
            }
        } else {
            // Normal registration flow
            setIsLoading(true);
            try {
                // Регистрируем пользователя и его предпочтения
                await axios.post('/register', updatedProfileData);
                addMessage("Thanks for completing your profile! Now you can create your weekly outfits plan.", 'bot');
                
                // Переходим на этап завершенной регистрации
                setStage(5);
            } catch (error) {
                console.error('Error during registration:', error);
                addMessage("Sorry, there was a problem completing your registration. Please try again.", 'bot');
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Function to update a single profile field
    const updateProfileField = async (field, value) => {
        try {
            await axios.post('/updateProfileField', {
                email,
                field,
                value
            });
            return true;
        } catch (error) {
            console.error(`Error updating profile field ${field}:`, error);
            throw error;
        }
    };

    // Function to proceed to the next incomplete field or back to the main interface
    const proceedToNextIncompleteField = (completedField) => {
        // Update the incomplete fields list
        const updatedIncompleteFields = incompleteFields.filter(field => field !== completedField);
        setIncompleteFields(updatedIncompleteFields);
        
        if (updatedIncompleteFields.length > 0) {
            // Still have more fields to complete
            addMessage("Thanks for updating your profile!", 'bot');
            promptForMissingField(updatedIncompleteFields[0]);
        } else {
            // All fields are now complete
            setCurrentField(null);
            setStage(5);
            addMessage("Great! Your profile is now complete. You can now create your weekly outfit plan!", 'bot');
            
            // Fetch weekly and daily outfits
            fetchWeeklyOutfits();
            fetchDailyOutfits();
        }
    };

    // Function to generate weekly outfits
    const generateWeeklyOutfits = async () => {
        if (!profileData) return;
        
        const { city, style, colors, gender } = profileData;
    
        try {
            setIsLoading(true);
            addMessage("I'm planning your outfits for the week. This might take a moment...", 'bot');
            
            // Request weekly outfits
            const response = await axios.post('/generateWeeklyOutfits', {
                city, style, colors, gender, email
            });
            
            if (response.data && response.data.outfits) {
                // Обрабатываем данные из ответа
                let outfitsData = response.data.outfits;
                
                // Если outfits пришел как строка, парсим его
                if (typeof outfitsData === 'string') {
                    try {
                        outfitsData = JSON.parse(outfitsData);
                    } catch (e) {
                        console.error('Error parsing outfits data:', e);
                        outfitsData = [];
                    }
                }
                
                // Если это пустой массив, отображаем сообщение
                if (!Array.isArray(outfitsData) || outfitsData.length === 0) {
                    addMessage("I'm sorry, I couldn't create enough suitable outfits for your style and preferences. Please try again later or adjust your profile settings.", 'bot');
                    setWeeklyOutfits([]);
                    setHasWeeklyOutfits(false);
                    return;
                }
                
                // Ensure each outfit has the required properties
                const sanitizedOutfits = outfitsData.map(outfit => ({
                    ...outfit,
                    description: outfit.summary || '',
                    collagePath: outfit.collagePath || '',
                    productLinks: outfit.productLinks && typeof outfit.productLinks === 'object' 
                        ? outfit.productLinks 
                        : {}
                }));
                
                setWeeklyOutfits(sanitizedOutfits);
                setHasWeeklyOutfits(true);
                
                if (response.data.expiryDate) {
                    setWeeklyOutfitsExpiryDate(new Date(response.data.expiryDate));
                }
                
                addMessage("Your weekly outfit plan is ready! I've created versatile looks that you can mix and match throughout the week. Each morning at 7:00 AM, you'll receive a daily outfit suggestion based on these looks.", 'bot');
                
                // Refresh daily outfits after generating weekly outfits
                fetchDailyOutfits();
            } else {
                addMessage("I'm sorry, I couldn't generate your weekly outfits at this time. Please try again later.", 'bot');
            }
        } catch (error) {
            console.error('Error generating weekly outfits:', error);
            addMessage('Sorry, there was an error generating your weekly outfits. Please try again.', 'bot');
            setWeeklyOutfits([]);
            setHasWeeklyOutfits(false);
        } finally {
            setIsLoading(false);
        }
    };

    // Check if user is authenticated and has a complete profile
    const isAuthenticated = stage === 5 && profileData;
    const isProfileComplete = isAuthenticated && incompleteFields.length === 0 && !currentField;

    // Format date helper
    const formatDate = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    // Get days remaining until expiry
    const getDaysRemaining = () => {
        if (!weeklyOutfitsExpiryDate) return 0;
        const now = new Date();
        const diffTime = weeklyOutfitsExpiryDate - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    return (
        <div className="daily-outfit-section">
            {!isAuthenticated || !isProfileComplete ? (
                <>
                    <div className="messages-container">
                        <div className="messages">
                            {renderMessages(messages)}
                        </div>
                    </div>
                    
                    <div className="buttons-section">
                        <RegisterSteps
                            stage={stage}
                            email={email}
                            setEmail={setEmail}
                            handleEmailSubmit={handleEmailSubmit}
                            handleGenderClick={handleGenderClick}
                            handleStyleClick={handleStyleClick}
                            handleCityClick={handleCityClick}
                            handleColorClick={handleColorClick}
                            betaError={betaError}
                        />
                    </div>
                </>
            ) : (
                <div className="outfit-dashboard">
                    {/* Weekly Outfits Section */}
                    <div className="weekly-outfits-section">
                        <h2 className="section-title">Weekly Outfit Plan</h2>
                        
                        {hasWeeklyOutfits ? (
                            <>
                                <div className="expiry-notice">
                                    Your plan refreshes in <span className="days-count">{getDaysRemaining()}</span> days
                                </div>
                                
                                <div className="outfits-grid">
                                    {weeklyOutfits.map((outfit, index) => (
                                        <OutfitCard 
                                            key={`weekly-${index}`}
                                            outfit={outfit}
                                            index={index}
                                            formatDate={formatDate}
                                            isDaily={false}
                                        />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="no-outfits-container">
                                <p className="no-outfits-message">
                                    You haven't created your weekly outfit plan yet. 
                                    Generate 3 versatile looks that you can mix and match throughout the week.
                                </p>
                                <WeeklyOutfitGenerator 
                                    generateWeeklyOutfits={generateWeeklyOutfits}
                                    isLoading={isLoading}
                                />
                            </div>
                        )}
                    </div>
                    
                    {/* Daily Outfits Section */}
                    <div className="daily-outfits-section">
                        <h2 className="section-title">Daily Outfit Suggestions</h2>
                        
                        {dailyOutfits.length > 0 ? (
                            <div className="daily-outfits-list">
                                {dailyOutfits.map((outfit, index) => (
                                    <OutfitCard 
                                        key={`daily-${index}`}
                                        outfit={outfit}
                                        index={index}
                                        formatDate={formatDate}
                                        isDaily={true}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="no-daily-outfits">
                                <p>
                                    {hasWeeklyOutfits 
                                        ? "You'll receive your first daily outfit suggestion tomorrow at 7:00 AM based on your weekly plan." 
                                        : "Create your weekly outfit plan to start receiving daily outfit suggestions."}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyOutfit;